import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { NameDataSource } from '@app/core.model';
import { CommonService } from '@app/services/common.service';
import { SpaceValidator } from '@app/validators/SpaceValidator';

import { ManagedClinicComponent } from '../managed-clinic.component';
import { Clinic, ClinicPayload } from '../managed-clinic.model';
import { ManagedClinicService } from '../managed-clinic.service';
import { MedicineThumbnail } from '@features/pharmacy/pharmacy.model';
import { MedicineImagePreviewComponent } from '@features/pharmacy/clinic-inventory/detail/medicine-image-preview/medicine-image-preview.component';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
  DialogType,
  DialogVariant,
} from '@shared/confirm-dialog/confirm-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-add-clinic',
  templateUrl: './add-clinic.component.html',
  styleUrls: ['./add-clinic.component.scss'],
})
export class AddClinicComponent implements OnInit {
  clinicFormGroup: FormGroup;
  companyFormGroup: FormGroup;
  today = new Date();
  pharmacyFormGroup: FormGroup;
  posFormGroup: FormGroup;
  cityList: NameDataSource[] = [];
  stateList: NameDataSource[];
  maxImageSizeInMb = 5;
  maxImageSizeInBytes = this.maxImageSizeInMb * 1024 * 1024;
  company: { cityList: NameDataSource[]; stateList: NameDataSource[] } = {
    cityList: [],
    stateList: [],
  };
  mode: string;
  isLinear: boolean = true;
  fssiNoThumbnail: MedicineThumbnail | null = null;
  licenseNo21Thumbnail: MedicineThumbnail | null = null;
  licenseNo20Thumbnail: MedicineThumbnail | null = null;
  digitalSignatureThumbnail: MedicineThumbnail | null = null;
  validImageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
  isClinicPharmacy = false;
  isClinicPharmacyPresent = false;
  pharmacyFileValidations = {
    licence20: true,
    licence21: true,
    fssai: true,
    digitalSignature: true,
  };

  dummyFormGroup = new FormGroup({});

  constructor(
    private snackBar: MatSnackBar,
    private fb: FormBuilder,
    private managedClinicServices: ManagedClinicService,
    private dialogRef: MatDialogRef<ManagedClinicComponent>,
    private commonService: CommonService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) private editData: Clinic
  ) {}

  ngOnInit(): void {
    this.editData ? (this.mode = 'edit') : (this.mode = 'add');

    this.posFormGroup = this.fb.group({
      posList: this.fb.array([]),
    });

    this.clinicFormGroup = this.fb.group({
      clinicName: ['', [Validators.required]],
      clinicDisplayName: ['', [Validators.required]],
      shortCodeName: [
        '',
        [Validators.required, SpaceValidator.cannotContainStartingSpace],
      ],
      reviewLink: [''],
      showUser: [true],
      showReceptionist: [true],
      mapLink: ['', [Validators.required]],
      address: this.fb.group({
        line1: [
          '',
          [
            Validators.required,
            SpaceValidator.cannotContainStartingSpace,
            SpaceValidator.cannotContainEndingSpace,
          ],
        ],
        line2: [
          '',
          [
            SpaceValidator.cannotContainStartingSpace,
            SpaceValidator.cannotContainEndingSpace,
          ],
        ],
        state: [undefined, [Validators.required]],
        city: [undefined, [Validators.required]],
        zipCode: [
          '',
          [
            Validators.required,
            Validators.min(100000),
            Validators.max(1000000),
            Validators.pattern('^[0-9]*$'),
          ],
        ],
      }),
      mobileNumberPrimary: [
        '',
        [Validators.required, Validators.pattern('^((\\+91-?)|0)?[0-9]{10}$')],
      ],
      mobileNumberSecondary: [
        '',
        [Validators.pattern('^((\\+91-?)|0)?[0-9]{10}$')],
      ],
      emailAddress: ['', [Validators.required, Validators.email]],
      whatsappNumber: [
        '',
        [Validators.required, Validators.pattern('^((\\+91-?)|0)?[0-9]{10}$')],
      ],
    });

    this.companyFormGroup = this.fb.group({
      companyName: ['', [Validators.required]],
      companyAddress: this.fb.group({
        line1: [
          '',
          [
            SpaceValidator.cannotContainStartingSpace,
            SpaceValidator.cannotContainEndingSpace,
          ],
        ],
        line2: [
          '',
          [
            SpaceValidator.cannotContainStartingSpace,
            SpaceValidator.cannotContainEndingSpace,
          ],
        ],
        state: [undefined, []],
        city: [undefined, []],
        zipCode: [
          '',
          [
            Validators.min(100000),
            Validators.max(1000000),
            Validators.pattern('^[0-9]*$'),
          ],
        ],
      }),
      companyGst: ['', [Validators.required]],
      companyCin: ['', [Validators.required]],
    });

    this.pharmacyFormGroup = this.fb.group({
      licence20: [
        '',
        [Validators.required, SpaceValidator.cannotContainStartingSpace],
      ],
      licence20Expiry: ['', [Validators.required]],
      licence21: [
        '',
        [Validators.required, SpaceValidator.cannotContainStartingSpace],
      ],
      licence21Expiry: ['', [Validators.required]],
      fssai: ['', [Validators.pattern(/^\d{14}$/)]],
      fssaiNoExpiry: [''],
      pharmacyMobileNumber: [
        '',
        [Validators.required, Validators.pattern('^((\\+91-?)|0)?[0-9]{10}$')],
      ],
      panNumber: [
        '',
        [Validators.required, Validators.pattern('^[A-Za-z0-9]{10}$')],
      ],
    });

    if (this.mode === 'edit') {
      this.setEditFormValue();
      this.setClinicPharmacyFormValue();
      this.patchPosDevices();
    }

    this.loadData();
  }

  get posList(): FormArray {
    return this.posFormGroup.get('posList') as FormArray;
  }

  createPOSFormGroup(): FormGroup {
    return this.fb.group({
      posName: ['', [Validators.required, Validators.maxLength(30)]],
      terminalId: [
        '',
        [Validators.required, Validators.pattern('^[a-zA-Z0-9]{8}$')],
      ],
      merchantId: [
        '',
        [Validators.required, Validators.pattern('^[a-zA-Z0-9]{15}$')],
      ],
      location: [[], Validators.required],
      posVendor: [''],
      added: [false],
    });
  }

  addPOS(): void {
    this.posList.push(this.createPOSFormGroup());
  }

  removePOS(index: number): void {
    this.posList.removeAt(index);
  }

  submitPOS(i: number): void {
    const form = this.posList.at(i);
    if (form.valid) {
      form.patchValue({ added: true });
    } else {
      form.markAllAsTouched();
    }
  }

  patchPosDevices() {
    this.getClinicDetail().subscribe((response) => {
      const editData = response.clinic;
      const posDevices = editData?.posDevices ?? [];

      if (posDevices.length > 0) {
        this.addPOS();

        const posFormArray = this.posFormGroup.get('posList') as FormArray;
        posFormArray.clear();

        posDevices.forEach((device: any) => {
          posFormArray.push(
            this.fb.group({
              id: [device.id],
              posName: [device.name],
              terminalId: [device.terminalId],
              merchantId: [device.merchantId],
              location: [device.locations],
              posVendor: [device.vendor],
              added: [true],
            })
          );
        });
      }
    });
  }

  setClinicPharmacyFormValue() {
    this.getClinicDetail().subscribe((response) => {
      const clinic = response.clinic;
      this.pharmacyFormGroup.patchValue({
        licence20: clinic.licence20?.licenceNo,
        licence20Expiry: clinic.licence20?.expiryDate,
        licence21: clinic.licence21?.licenceNo,
        licence21Expiry: clinic.licence21?.expiryDate,
        fssai: clinic.fssai?.licenceNo,
        fssaiNoExpiry: clinic.fssai?.expiryDate,
        pharmacyMobileNumber: clinic.pharmacyMobileNumber,
        panNumber: clinic.panNumber,
      });

      this.licenseNo20Thumbnail = clinic.licence21?.imageUrl
        ? {
            url: clinic.licence20?.imageUrl,
            name: clinic.licence20?.image?.originalFileName,
            isPdf: clinic.licence20?.image?.mimeType === 'application/pdf',
          }
        : null;

      this.licenseNo21Thumbnail = clinic.licence21?.imageUrl
        ? {
            url: clinic.licence21?.imageUrl,
            name: clinic.licence21?.image?.originalFileName,
            isPdf: clinic.licence21?.image?.mimeType === 'application/pdf',
          }
        : null;
      this.fssiNoThumbnail = clinic.fssai?.imageUrl
        ? {
            url: clinic.fssai?.imageUrl,
            name: clinic.fssai?.image?.originalFileName,
            isPdf: clinic.fssai?.image?.mimeType === 'application/pdf',
          }
        : null;
      this.digitalSignatureThumbnail = clinic.digitalSignatureUrl
        ? {
            url: clinic.digitalSignatureUrl,
            name: clinic.digitalSignature?.originalFileName,
            isPdf: clinic.digitalSignature?.mimeType === 'application/pdf',
          }
        : null;

      if ('hasPharmacy' in response.clinic) {
        this.isClinicPharmacy = response.clinic.hasPharmacy;
        this.isClinicPharmacyPresent = response.clinic.hasPharmacy;
      }
    });
  }

  setEditFormValue() {
    this.clinicFormGroup.patchValue({
      reviewLink: this.editData.reviewLink,
    });

    this.clinicFormGroup.patchValue({
      clinicName: this.editData.name,
      clinicDisplayName: this.editData?.displayName,
      shortCodeName: this.editData?.shortCode,
      showUser: this.editData.showUser ?? true,
      showReceptionist: this.editData.showReceptionist ?? true,
      address: {
        line1: this.editData.address.line1,
        line2: this.editData.address.line2,
        zipCode: this.editData.address.zipCode,
      },
      mobileNumberPrimary: this.editData.mobileNumber,
      mobileNumberSecondary: this.editData.mobileNumberSecondary,
      emailAddress: this.editData.emailAddress,
      mapLink: this.editData.mapLink,
      whatsappNumber: this.editData?.whatsapp?.phoneNumber,
    });

    this.companyFormGroup.patchValue({
      companyName: this.editData?.company?.name,
      companyAddress: {
        line1: this.editData?.company?.address?.line1,
        line2: this.editData?.company?.address?.line2,
        zipCode: this.editData?.company?.address?.zipCode,
      },
      companyCin: this.editData?.company?.cinNumber,
      companyGst: this.editData?.company?.gstNumber,
    });

    this.clinicFormGroup.patchValue({
      address: {
        state: this.editData.address.state.id,
      },
    });

    this.companyFormGroup.patchValue({
      companyAddress: {
        state: this.editData?.company?.address?.state?.id,
        city: this.editData?.company?.address?.city?.id,
      },
    });

    this.getCityListByStateId(this.editData.address?.state?.id, () => {
      this.clinicFormGroup.patchValue({
        address: {
          city: this.editData?.address?.city?.id
            ? this.editData?.address?.city?.id
            : '',
        },
      });
    });

    this.getCompanyCityListByStateId(
      this.editData?.company?.address?.state?.id ?? '',
      () => {
        this.companyFormGroup.patchValue({
          companyAddress: {
            city: this.editData?.company?.address?.city?.id
              ? this.editData?.company?.address?.city?.id
              : '',
          },
        });
      }
    );
  }

  close() {
    this.dialogRef.close(false);
  }

  openImagePreview(imageUrl = '') {
    this.dialog.open(MedicineImagePreviewComponent, {
      data: {
        images: [{ url: imageUrl }],
      },
    });
  }

  private loadData() {
    this.managedClinicServices.getStateList().subscribe((response) => {
      this.stateList = response.states;
      this.company.stateList = response.states;
    });
  }

  getClinicDetail() {
    return this.managedClinicServices.getClinicDetail(this.editData.id);
  }

  onSelectionChange(id: string) {
    if (id) {
      this.getCityListByStateId(id);
    }
    this.clinicFormGroup.patchValue({
      address: {
        city: '',
      },
    });
  }

  onSelectionChangeOnCompany(id: string) {
    if (id) {
      this.getCompanyCityListByStateId(id);
    }
    this.companyFormGroup.patchValue({
      companyAddress: {
        city: '',
      },
    });
  }

  getCompanyCityListByStateId(
    stateId: string,
    onCityListBind: (() => void) | undefined = undefined
  ) {
    this.managedClinicServices
      .getCityListById(stateId)
      .subscribe((response) => {
        this.company.cityList = response.cities;
        if (onCityListBind) onCityListBind();
      });
  }

  getCityListByStateId(
    stateId: string,
    onCityListBind: (() => void) | undefined = undefined
  ) {
    this.managedClinicServices
      .getCityListById(stateId)
      .subscribe((response) => {
        this.cityList = response.cities;
        if (onCityListBind) onCityListBind();
      });
  }

  validatePharmacyFiles() {
    let isValid = true;
    this.pharmacyFileValidations.licence20 = this.licenseNo20Thumbnail
      ? true
      : false;
    this.pharmacyFileValidations.licence21 = this.licenseNo21Thumbnail
      ? true
      : false;
    this.pharmacyFileValidations.digitalSignature = this
      .digitalSignatureThumbnail
      ? true
      : false;
    if (
      !this.licenseNo20Thumbnail ||
      !this.licenseNo21Thumbnail ||
      !this.digitalSignatureThumbnail
    ) {
      isValid = false;
    }
    return isValid;
  }

  add() {
    // if (!this.clinicFormGroup.valid || !this.companyFormGroup.valid) {
    //   return;
    // }
    const posFormArray = this.posFormGroup.get('posList') as FormArray;
    let hasInvalidPOS = false;

    if (posFormArray && posFormArray.controls) {
      posFormArray.controls.forEach((group: AbstractControl) => {
        const pos = group.getRawValue();
        if (pos.added && (!pos.location || pos.location.length === 0)) {
          group.get('location')?.setErrors({ required: true });
          group.get('location')?.markAsTouched();
          hasInvalidPOS = true;
        }
      });
    }

    if (hasInvalidPOS) {
      return;
    }

    if (this.posFormGroup.invalid) {
      this.posFormGroup.markAllAsTouched();
      return;
    }

    if (this.isClinicPharmacy) {
      this.pharmacyFormGroup.markAllAsTouched();
      let isValid = this.pharmacyFormGroup.valid;
      isValid = this.validatePharmacyFiles();
      if (!isValid) return;
    }

    const posDevices = this.posList.value
      .filter((pos: any) => pos.added)
      .map((pos: any) => ({
        ...(pos.id && { id: pos.id }),
        name: pos.posName,
        terminalId: pos.terminalId,
        merchantId: pos.merchantId,
        vendor: pos.posVendor,
        location: pos.location,
      }));

    if (this.mode === 'add') {
      const makePayload: ClinicPayload = {
        name: this.clinicFormGroup.value.clinicName,
        displayName: this.clinicFormGroup.value.clinicDisplayName,
        shortCode: this.clinicFormGroup.value.shortCodeName,
        reviewLink: this.clinicFormGroup.value.reviewLink,
        mapLink: this.clinicFormGroup.value.mapLink,
        showUser: this.clinicFormGroup.value.showUser,
        showReceptionist: this.clinicFormGroup.value.showReceptionist,
        address: this.clinicFormGroup.value.address,
        mobileNumber: this.clinicFormGroup.value.mobileNumberPrimary,
        mobileNumberSecondary:
          this.clinicFormGroup.value.mobileNumberSecondary ?? '',
        emailAddress: this.clinicFormGroup.value.emailAddress,
        companyName: this.companyFormGroup.value.companyName,
        companyAddress: this.companyFormGroup.value.companyAddress,
        companyGst: this.companyFormGroup.value.companyGst,
        companyCin: this.companyFormGroup.value.companyCin,
        whatsapp: {
          phoneNumber: this.clinicFormGroup.value.whatsappNumber,
        },
        hasPharmacy: this.isClinicPharmacy,
        ...(this.isClinicPharmacy && {
          licence20: {
            licenceNo: this.pharmacyFormGroup.value.licence20,
            expiryDate: this.pharmacyFormGroup.value.licence20Expiry,
            image: {
              name: this.licenseNo20Thumbnail?.name,
              dataURI: this.licenseNo20Thumbnail?.dataURI,
            },
          },

          fssai: {
            ...(this.pharmacyFormGroup.value.fssai && {
              licenceNo: this.pharmacyFormGroup.value.fssai,
            }),
            ...(this.pharmacyFormGroup.value.fssaiNoExpiry && {
              expiryDate: this.pharmacyFormGroup.value.fssaiNoExpiry,
            }),
            ...(this.fssiNoThumbnail?.dataURI && {
              image: {
                name: this.fssiNoThumbnail?.name,
                dataURI: this.fssiNoThumbnail?.dataURI,
              },
            }),
          },

          licence21: {
            licenceNo: this.pharmacyFormGroup.value.licence21,
            expiryDate: this.pharmacyFormGroup.value.licence21Expiry,
            image: {
              name: this.licenseNo21Thumbnail?.name,
              dataURI: this.licenseNo21Thumbnail?.dataURI,
            },
          },

          digitalSignature: {
            name: this.digitalSignatureThumbnail?.name,
            dataURI: this.digitalSignatureThumbnail?.dataURI,
          },

          pharmacyMobileNumber:
            this.pharmacyFormGroup.value.pharmacyMobileNumber,
          panNumber: this.pharmacyFormGroup.value.panNumber,
        }),
        posDevices,
      };
      this.managedClinicServices.addClinic(makePayload).subscribe(() => {
        this.snackBar.open('Clinic added successfully', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-success'],
        });
        this.dialogRef.close(true);
      });

      this.clinicFormGroup.reset();
    } else if (this.mode === 'edit' && this.editData.id) {
      if (this.isClinicPharmacy && this.pharmacyFormGroup.invalid) return;
      const makePayload: ClinicPayload = {
        name: this.clinicFormGroup.value.clinicName,
        displayName: this.clinicFormGroup.value.clinicDisplayName,
        shortCode: this.clinicFormGroup.value.shortCodeName,
        reviewLink: this.clinicFormGroup.value.reviewLink,
        showUser: this.clinicFormGroup.value.showUser,
        showReceptionist: this.clinicFormGroup.value.showReceptionist,
        mapLink: this.clinicFormGroup.value.mapLink,
        address: this.clinicFormGroup.value.address,
        mobileNumber: this.clinicFormGroup.value.mobileNumberPrimary,
        mobileNumberSecondary:
          this.clinicFormGroup.value.mobileNumberSecondary ?? '',
        emailAddress: this.clinicFormGroup.value.emailAddress,
        companyName: this.companyFormGroup.value.companyName,
        companyAddress: this.companyFormGroup.value.companyAddress,
        companyGst: this.companyFormGroup.value.companyGst,
        companyCin: this.companyFormGroup.value.companyCin,
        whatsapp: {
          phoneNumber: this.clinicFormGroup.value.whatsappNumber,
        },

        hasPharmacy: this.isClinicPharmacy,
        ...(this.isClinicPharmacy && {
          licence20: {
            licenceNo: this.pharmacyFormGroup.value.licence20,
            expiryDate: this.pharmacyFormGroup.value.licence20Expiry,
            ...(this.licenseNo20Thumbnail?.dataURI && {
              image: {
                name: this.licenseNo20Thumbnail?.name,
                dataURI: this.licenseNo20Thumbnail?.dataURI,
              },
            }),
          },

          licence21: {
            licenceNo: this.pharmacyFormGroup.value.licence21,
            expiryDate: this.pharmacyFormGroup.value.licence21Expiry,
            ...(this.licenseNo21Thumbnail?.dataURI && {
              image: {
                name: this.licenseNo21Thumbnail?.name,
                dataURI: this.licenseNo21Thumbnail?.dataURI,
              },
            }),
          },

          fssai: {
            ...(this.pharmacyFormGroup.value.fssai && {
              licenceNo: this.pharmacyFormGroup.value.fssai,
            }),
            ...(this.pharmacyFormGroup.value.fssaiNoExpiry && {
              expiryDate: this.pharmacyFormGroup.value.fssaiNoExpiry,
            }),
            ...(!this.fssiNoThumbnail && {
              image: {
                name: '',
                dataURI: '',
              },
            }),
            ...(this.fssiNoThumbnail?.dataURI && {
              image: {
                name: this.fssiNoThumbnail.name,
                dataURI: this.fssiNoThumbnail.dataURI,
              },
            }),
          },

          ...(this.digitalSignatureThumbnail?.dataURI && {
            digitalSignature: {
              name: this.digitalSignatureThumbnail?.name,
              dataURI: this.digitalSignatureThumbnail?.dataURI,
            },
          }),

          pharmacyMobileNumber:
            this.pharmacyFormGroup.value.pharmacyMobileNumber,
          panNumber: this.pharmacyFormGroup.value.panNumber,
        }),
        posDevices,
      };

      this.managedClinicServices
        .updateClinic(makePayload, this.editData.id)
        .subscribe({
          next: () => {
            this.snackBar.open('Clinic updated successfully', 'Close', {
              duration: 3000,
              panelClass: ['snackbar-success'],
            });
            this.dialogRef.close(true);
          },
          error: (err: { error: { code: string } }) => {
            this.commonService.FailedAlertDialog({
              dialog: this.dialog,
              message: `Errors.${err.error.code}`,
            });
          },
        });
    }
  }

  async onFileSelected(event: Event, field: string) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[input.files.length - 1];

      // Check file type
      if (!this.validImageTypes.includes(file.type)) {
        input.value = ''; // Reset input field
        return this.validationAlertDialog(LocalTranslations.InvalidImageType);
      }

      // Check file size
      if (file.size > this.maxImageSizeInBytes) {
        input.value = ''; // Reset input field
        return this.validationAlertDialog(
          `Image can be uploaded of max size ${this.maxImageSizeInMb} MB.`
        );
      }

      const thumbnail: MedicineThumbnail = {
        name: file.name,
        dataURI: await this.fileToDataUri(file),
        isPdf: file.type === 'application/pdf',
      };

      if (file.type === 'application/pdf') {
        const pdfUrl = URL.createObjectURL(file);
        thumbnail.localPdfUrl = pdfUrl;
      }

      switch (field) {
        case 'licenseNo20':
          this.licenseNo20Thumbnail = thumbnail;
          this.pharmacyFileValidations.licence20 = true;
          break;
        case 'licenseNo21':
          this.licenseNo21Thumbnail = thumbnail;
          this.pharmacyFileValidations.licence21 = true;
          break;
        case 'fssiNo':
          this.fssiNoThumbnail = thumbnail;
          break;
        case 'digitalSignature':
          this.digitalSignatureThumbnail = thumbnail;
          this.pharmacyFileValidations.digitalSignature = true;
          break;
      }

      input.value = '';
    }
  }

  openPdf(pdfUrl = '') {
    const anchor = document.createElement('a');
    anchor.href = pdfUrl;
    anchor.target = '_blank';
    anchor.click();

    // URL.revokeObjectURL(pdfUrl);
  }

  fileToDataUri(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const dataUri = reader.result as string;
        resolve(dataUri);
      };

      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };

      reader.readAsDataURL(file);
    });
  }

  removeFssiImage() {
    this.fssiNoThumbnail = null;
  }

  removeDigitalSignatureThumbnail() {
    this.digitalSignatureThumbnail = null;
  }

  removeLicenseNo21mage() {
    this.licenseNo21Thumbnail = null;
  }

  removeLicenseNo20mage() {
    this.licenseNo20Thumbnail = null;
  }

  private validationAlertDialog(message: string): void {
    const dialogData = new ConfirmDialogModel(
      'Validation Failed',
      message,
      DialogVariant.OK,
      DialogType.Warning
    );

    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData,
    });
  }
}

enum LocalTranslations {
  Success = 'Success',
  InvalidFileTypeMessage = 'InvalidFileTypeMessage',
  SomethingWentWrong = 'SomethingWentWrong',
  InvalidImageType = 'Invalid format. Formats allowed - png, jpg, jpeg and pdf.',
  InvalidImageSize = 'Image can be uploaded of max size 10 MB.',
  FormValidationAlert = 'Please review all fields again.',
}

export enum PosLocation {
  FrontDesk = 1,
  Lab = 2,
  Pharmacy = 3,
}
